import React, { useState, useEffect, useMemo, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  ArcElement, Title, Tooltip, Legend,
} from "chart.js";
import { jwtDecode } from "jwt-decode";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import Papa from "papaparse";
import {
  Plus, X, Edit2, Trash2, Download, Search, Upload,
  TrendingUp, TrendingDown, Receipt, DollarSign, Calendar,
  ChevronLeft, ChevronRight, BarChart2, RefreshCw, AlertCircle,
  Tag, Repeat, FileText, CheckCircle, Clock, Target,
  ArrowUpDown, ArrowUp, ArrowDown, LayoutDashboard, List,
  PieChart, Image, AlertTriangle, FileUp,
} from "lucide-react";
import api from "../utils/api";
import { todayLocal } from "../utils/dates";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

/* ─── Constants ─────────────────────────────────────────────────────────── */

const CATEGORIES = [
  { value: "Food & Beverages", color: "#f97316", badge: "bg-orange-100 text-orange-700 border-orange-200" },
  { value: "Staff & Salaries", color: "#8b5cf6", badge: "bg-purple-100 text-purple-700 border-purple-200" },
  { value: "Utilities",        color: "#06b6d4", badge: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  { value: "Rent",             color: "#ef4444", badge: "bg-red-100 text-red-700 border-red-200" },
  { value: "Maintenance",      color: "#f59e0b", badge: "bg-amber-100 text-amber-700 border-amber-200" },
  { value: "Marketing",        color: "#ec4899", badge: "bg-pink-100 text-pink-700 border-pink-200" },
  { value: "Equipment",        color: "#10b981", badge: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "Miscellaneous",    color: "#6b7280", badge: "bg-gray-100 text-gray-600 border-gray-200" },
];

const PERIODS = [
  { label: "All Time", value: "all" },
  { label: "Today",    value: "today" },
  { label: "7 Days",   value: "7" },
  { label: "30 Days",  value: "30" },
  { label: "3 Months", value: "90" },
  { label: "1 Year",   value: "365" },
];

const TABS = [
  { key: "overview",  label: "Overview",  icon: LayoutDashboard },
  { key: "expenses",  label: "Expenses",  icon: List },
  { key: "budgets",   label: "Budgets",   icon: Target },
  { key: "reports",   label: "Reports",   icon: FileText },
];

// A function so the default date is "today" when the form opens, not when the page loaded
const emptyForm = () => ({
  date: todayLocal(),
  title: "", price: "", category: "Miscellaneous",
  description: "", tags: [], isRecurring: false,
  recurringFrequency: "monthly", receiptUrl: null, status: "approved",
  paymentMode: "paid", vendorName: "",
});

const getCat  = (v) => CATEGORIES.find(c => c.value === v) || CATEGORIES[CATEGORIES.length - 1];
const fmt     = (n) => parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => {
  if (!s) return "—";
  const [y, m, d] = s.split("T")[0].split("-");
  return `${d} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} ${y}`;
};

/* ─── Component ─────────────────────────────────────────────────────────── */

const Expensises = () => {
  /* state */
  const [data,    setData]    = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId,  setUserId]  = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  /* modal */
  const [showModal,    setShowModal]    = useState(false);
  const [form,         setForm]         = useState(emptyForm);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [updateId,     setUpdateId]     = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput,     setTagInput]     = useState("");
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [dupWarning,   setDupWarning]   = useState(null);
  const receiptRef = useRef();

  /* bulk import */
  const [showImport,    setShowImport]    = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importing,     setImporting]     = useState(false);
  const importRef = useRef();

  /* filters */
  const [search,         setSearch]         = useState("");
  const [period,         setPeriod]         = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter,   setStatusFilter]   = useState("all");

  /* table */
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page,    setPage]    = useState(1);
  const PAGE = 10;

  /* overview charts */
  const [chartView, setChartView] = useState("bar"); // "bar" | "donut"

  /* report */
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo,   setReportTo]   = useState("");

  /* budget editing */
  const [editingBudget, setEditingBudget] = useState(null);
  const [budgetInput,   setBudgetInput]   = useState("");

  /* ── init ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try { const d = jwtDecode(token); if (d.user) setUserId(d.user.id); } catch (_) {}
    }
  }, []);

  const fetchData = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const [expRes, budRes] = await Promise.all([
        api.get(`expenses/${userId}`),
        api.get(`budgets/${userId}`),
      ]);
      setData(expRes.data || []);
      setBudgets(budRes.data || []);
    } catch { toast.error("Failed to load expenses"); }
    finally   { setLoading(false); }
  };

  useEffect(() => { if (userId) fetchData(); }, [userId]); // eslint-disable-line

  /* ── derived data ── */
  const filteredData = useMemo(() => {
    let items = [...data];
    if (period !== "all") {
      const now = new Date();
      if (period === "today") {
        const t = todayLocal();
        items = items.filter(i => i.date?.startsWith(t));
      } else {
        const days = +period;
        items = items.filter(i => (now - new Date(i.date)) / 86400000 <= days);
      }
    }
    if (categoryFilter !== "all") items = items.filter(i => (i.category || "Miscellaneous") === categoryFilter);
    if (statusFilter   !== "all") items = items.filter(i => (i.status || "approved") === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(i =>
        i.title?.toLowerCase().includes(q) ||
        i.description?.toLowerCase().includes(q) ||
        i.category?.toLowerCase().includes(q) ||
        i.tags?.some(t => t.toLowerCase().includes(q))
      );
    }
    items.sort((a, b) => {
      let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      if (sortKey === "price") { av = +av || 0; bv = +bv || 0; }
      else if (sortKey === "date") { av = new Date(av); bv = new Date(bv); }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      return av < bv ? (sortDir === "asc" ? -1 : 1) : av > bv ? (sortDir === "asc" ? 1 : -1) : 0;
    });
    return items;
  }, [data, period, categoryFilter, statusFilter, search, sortKey, sortDir]);

  const stats = useMemo(() => {
    const total   = filteredData.reduce((s, i) => s + (+i.price || 0), 0);
    const highest = filteredData.reduce((m, i) => Math.max(m, +i.price || 0), 0);
    const todayStr = todayLocal();
    const todayTotal = data.filter(i => i.date?.startsWith(todayStr)).reduce((s, i) => s + (+i.price || 0), 0);
    const now = new Date();
    const mKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    const monthTotal = data.filter(i => i.date?.startsWith(mKey)).reduce((s, i) => s + (+i.price || 0), 0);
    return { total, highest, todayTotal, monthTotal, count: filteredData.length };
  }, [filteredData, data]);

  /* month-over-month */
  const mom = useMemo(() => {
    const now = new Date();
    const cur  = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    const prev = new Date(now.getFullYear(), now.getMonth()-1, 1);
    const last = `${prev.getFullYear()}-${String(prev.getMonth()+1).padStart(2,"0")}`;
    const thisM = data.filter(i => i.date?.startsWith(cur)).reduce((s,i)=>s+(+i.price||0),0);
    const lastM = data.filter(i => i.date?.startsWith(last)).reduce((s,i)=>s+(+i.price||0),0);
    const pct = lastM === 0 ? null : ((thisM - lastM) / lastM) * 100;
    return { thisM, lastM, pct };
  }, [data]);

  /* bar chart */
  const barData = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5-i), 1);
      return { label: d.toLocaleString("default",{month:"short",year:"2-digit"}), key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, total: 0 };
    });
    data.forEach(item => {
      const k = item.date?.split("T")[0]?.substring(0,7);
      const m = months.find(mo => mo.key === k);
      if (m) m.total += +item.price || 0;
    });
    const curKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    return {
      labels: months.map(m => m.label),
      datasets: [{ label:"₹", data: months.map(m => m.total),
        backgroundColor: months.map(m => m.key===curKey?"rgba(99,102,241,0.9)":"rgba(99,102,241,0.45)"),
        borderColor:"rgba(99,102,241,1)", borderWidth:1.5, borderRadius:6 }],
    };
  }, [data]);

  /* donut chart */
  const donutData = useMemo(() => {
    const tots = {};
    filteredData.forEach(i => { const c=i.category||"Miscellaneous"; tots[c]=(tots[c]||0)+(+i.price||0); });
    const cats = Object.keys(tots).filter(c=>tots[c]>0);
    return {
      labels: cats,
      datasets:[{ data: cats.map(c=>tots[c]),
        backgroundColor: cats.map(c=>getCat(c).color+"CC"),
        borderColor: cats.map(c=>getCat(c).color),
        borderWidth: 2 }],
    };
  }, [filteredData]);

  /* budget progress */
  const budgetProgress = useMemo(() => {
    const now = new Date();
    const mKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    return CATEGORIES.map(cat => {
      const b = budgets.find(b => b.category === cat.value);
      const spent = data.filter(i=>i.date?.startsWith(mKey)&&(i.category||"Miscellaneous")===cat.value).reduce((s,i)=>s+(+i.price||0),0);
      const limit = b?.monthlyLimit || 0;
      const pct   = limit > 0 ? Math.min((spent/limit)*100, 100) : 0;
      return { ...cat, spent, limit, pct, isOver: limit>0&&spent>limit, budgetDoc: b };
    });
  }, [data, budgets]);

  /* report range data */
  const reportData = useMemo(() => {
    if (!reportFrom && !reportTo) return filteredData;
    return data.filter(i => {
      const d = i.date?.split("T")[0] || "";
      if (reportFrom && d < reportFrom) return false;
      if (reportTo   && d > reportTo)   return false;
      return true;
    }).sort((a,b)=> new Date(a.date)-new Date(b.date));
  }, [data, filteredData, reportFrom, reportTo]);

  /* pagination */
  const totalPages = Math.ceil(filteredData.length / PAGE);
  const pageData   = filteredData.slice((page-1)*PAGE, page*PAGE);

  /* ── handlers ── */
  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d=>d==="asc"?"desc":"asc");
    else { setSortKey(key); setSortDir("desc"); }
    setPage(1);
  };

  const openAdd = () => { setForm(emptyForm()); setIsUpdateMode(false); setUpdateId(null); setDupWarning(null); setShowModal(true); };
  const openEdit = (item) => {
    setForm({
      date: item.date?.split("T")[0] || todayLocal(),
      title: item.title||"", price: item.price||"", category: item.category||"Miscellaneous",
      description: item.description||"", tags: item.tags||[], isRecurring: item.isRecurring||false,
      recurringFrequency: item.recurringFrequency||"monthly", receiptUrl: item.receiptUrl||null,
      status: item.status||"approved", paymentMode: item.paymentMode||"paid", vendorName: item.vendorName||"",
    });
    setIsUpdateMode(true); setUpdateId(item._id); setDupWarning(null); setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setForm(emptyForm()); setTagInput(""); setIsUpdateMode(false); setUpdateId(null); setDupWarning(null); };

  const checkDuplicate = () => {
    const sevenAgo = new Date(); sevenAgo.setDate(sevenAgo.getDate()-7);
    return data.find(item => {
      if (isUpdateMode && item._id === updateId) return false;
      const iDate = new Date(item.date);
      return iDate >= sevenAgo && (parseFloat(item.price)===parseFloat(form.price) || item.title?.toLowerCase()===form.title?.toLowerCase());
    });
  };

  const checkBudgetAlert = (category, amount, newData) => {
    const b = budgets.find(b=>b.category===category);
    if (!b||!b.monthlyLimit) return;
    const now = new Date();
    const mKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
    const spent = (newData||data).filter(i=>i.date?.startsWith(mKey)&&(i.category||"Miscellaneous")===category).reduce((s,i)=>s+(+i.price||0),0) + (+amount);
    if (spent > b.monthlyLimit) toast.warning(`Budget exceeded for ${category}! Spent ₹${fmt(spent)} / Limit ₹${fmt(b.monthlyLimit)}`, { icon: "⚠️" });
    else if (spent/b.monthlyLimit > 0.8) toast.info(`80% of ${category} budget used this month`);
  };

  const handleChange = (e) => { const {id,value}=e.target; setForm(p=>({...p,[id]:value})); };

  const handleTagKey = (e) => {
    if (["Enter","Tab",","].includes(e.key)) {
      e.preventDefault();
      const t = tagInput.trim().replace(/,$/,"");
      if (t && !form.tags.includes(t)) setForm(p=>({...p,tags:[...p.tags,t]}));
      setTagInput("");
    }
  };
  const removeTag = (t) => setForm(p=>({...p,tags:p.tags.filter(x=>x!==t)}));

  const handleReceiptUpload = async (file) => {
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData(); fd.append("receipt", file);
      const res = await api.post("expense-receipt", fd, { headers:{"Content-Type":"multipart/form-data"} });
      setForm(p=>({...p,receiptUrl:res.data.url}));
      toast.success("Receipt uploaded!");
    } catch { toast.error("Failed to upload receipt"); }
    finally { setUploadingReceipt(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()||!form.price) { toast.error("Title and amount are required"); return; }
    if (form.paymentMode==="credit"&&!form.vendorName.trim()) { toast.error("Vendor name is required for credit expenses"); return; }
    const dup = checkDuplicate();
    if (dup && !dupWarning) { setDupWarning(dup); return; }
    setIsSubmitting(true);
    try {
      if (isUpdateMode) {
        await api.put(`expens/${updateId}`, form);
        setData(prev=>prev.map(i=>i._id===updateId?{...i,...form}:i));
        toast.success("Expense updated!");
      } else {
        const newForm = {...form, srno: data.length+1};
        const res = await api.post(`expenses/${userId}`, newForm);
        const newItem = res.data?.contact || res.data;
        setData(prev=>[newItem,...prev]);
        checkBudgetAlert(form.category, form.price);
        toast.success("Expense added!");
      }
      closeModal(); fetchData();
    } catch { toast.error(isUpdateMode?"Error updating":"Error adding"); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title:"Delete Expense?", text:"This cannot be undone.", icon:"warning",
      showCancelButton:true, confirmButtonColor:"#ef4444", cancelButtonColor:"#6b7280",
      confirmButtonText:"Delete", cancelButtonText:"Cancel" });
    if (result.isConfirmed) {
      try { await api.delete(`expens/${id}`); setData(prev=>prev.filter(i=>i._id!==id)); toast.success("Deleted!"); }
      catch { toast.error("Error deleting"); }
    }
  };

  const handleApprove = async (item) => {
    try {
      await api.put(`expens/${item._id}`, {...item, status:"approved"});
      setData(prev=>prev.map(i=>i._id===item._id?{...i,status:"approved"}:i));
      toast.success("Expense approved!");
    } catch { toast.error("Error approving"); }
  };

  const handleExport = () => {
    if (!filteredData.length) { toast.warning("No data to export"); return; }
    const headers = ["Sr.No.","Date","Category","Title","Amount (₹)","Tags","Status","Description"];
    const rows = filteredData.map((item,idx)=>[
      idx+1, item.date?.split("T")[0]||"", item.category||"Miscellaneous",
      item.title||"", item.price||0, (item.tags||[]).join("; "),
      item.status||"approved", item.description||"",
    ]);
    const csv = [headers,...rows].map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href=url; a.download=`expenses_${todayLocal()}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success("Exported!");
  };

  /* ── Budget handlers ── */
  const saveBudget = async (category) => {
    const limit = parseFloat(budgetInput);
    if (isNaN(limit)||limit<0) { toast.error("Enter a valid amount"); return; }
    try {
      const res = await api.post(`budgets/${userId}`, { category, monthlyLimit: limit });
      setBudgets(prev=>{
        const exists = prev.find(b=>b.category===category);
        return exists ? prev.map(b=>b.category===category?res.data:b) : [...prev,res.data];
      });
      toast.success(`Budget set for ${category}`);
      setEditingBudget(null); setBudgetInput("");
    } catch { toast.error("Error saving budget"); }
  };

  /* ── Bulk import ── */
  const handleCSVParse = (e) => {
    const file = e.target.files[0]; if (!file) return;
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (r) => { setImportPreview(r.data.filter(row=>row.Title||row.title||row.Amount||row.amount)); },
      error: () => toast.error("Failed to parse CSV"),
    });
  };

  const handleBulkImport = async () => {
    if (!importPreview.length) return;
    setImporting(true); let ok=0, fail=0;
    for (const row of importPreview) {
      try {
        await api.post(`expenses/${userId}`, {
          srno: data.length+ok+1,
          date: row.Date||row.date||todayLocal(),
          title: row.Title||row.title||"Imported",
          price: parseFloat(row["Amount (₹)"]||row.Amount||row.price||0),
          category: row.Category||row.category||"Miscellaneous",
          description: row.Description||row.description||"",
          tags: [], status:"approved",
        });
        ok++;
      } catch { fail++; }
    }
    toast.success(`Imported ${ok}${fail?`, ${fail} failed`:""}`);
    setShowImport(false); setImportPreview([]); fetchData(); setImporting(false);
  };

  /* ── PDF report ── */
  const generatePDF = () => {
    if (!reportData.length) { toast.warning("No data in range"); return; }
    const doc = new jsPDF();
    const total = reportData.reduce((s,i)=>s+(+i.price||0),0);

    doc.setFontSize(20); doc.setTextColor(99,102,241);
    doc.text("Expense Report", 14, 22);
    doc.setFontSize(10); doc.setTextColor(100,100,100);
    doc.text(`Period: ${reportFrom||"All"} → ${reportTo||"Today"}`, 14, 31);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}  |  Records: ${reportData.length}`, 14, 37);
    doc.setFontSize(13); doc.setTextColor(30,30,30);
    doc.text(`Total: Rs.${fmt(total)}`, 14, 46);

    /* category summary */
    const catTotals = {};
    reportData.forEach(i=>{ const c=i.category||"Misc"; catTotals[c]=(catTotals[c]||0)+(+i.price||0); });
    let sy = 56;
    doc.setFontSize(9); doc.setTextColor(80,80,80);
    Object.entries(catTotals).forEach(([c,v])=>{
      doc.text(`• ${c}: Rs.${fmt(v)}`, 14, sy); sy+=6;
    });

    /* table */
    const cols = ["#","Date","Category","Title","Amount (Rs.)"];
    const cw   = [10,28,40,72,30];
    let y = sy + 8;
    doc.setFillColor(230,232,255); doc.rect(14, y-5, 182, 8,"F");
    doc.setFontSize(8); doc.setTextColor(60,60,60);
    let x=14; cols.forEach((h,i)=>{ doc.text(h,x+1,y); x+=cw[i]; });
    y+=6;

    reportData.forEach((item,idx)=>{
      if (y>275) { doc.addPage(); y=20; }
      if (idx%2===0) { doc.setFillColor(248,249,255); doc.rect(14,y-5,182,8,"F"); }
      doc.setFontSize(7.5); doc.setTextColor(30,30,30);
      x=14;
      [String(idx+1), item.date?.split("T")[0]||"", (item.category||"Misc").substring(0,18), (item.title||"").substring(0,30), `Rs.${parseFloat(item.price||0).toFixed(2)}`]
        .forEach((cell,i)=>{ doc.text(String(cell),x+1,y); x+=cw[i]; });
      y+=7;
    });

    doc.save(`expense-report-${todayLocal()}.pdf`);
    toast.success("PDF downloaded!");
  };

  /* ── Sort icon ── */
  const SortIcon = ({col}) => {
    if (sortKey!==col) return <ArrowUpDown size={13} className="text-gray-400 inline ml-1"/>;
    return sortDir==="asc"
      ? <ArrowUp size={13} className="text-indigo-600 inline ml-1"/>
      : <ArrowDown size={13} className="text-indigo-600 inline ml-1"/>;
  };

  /* ── Loading ── */
  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-600 border-t-transparent"/>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════════ */
  /*  RENDER                                                               */
  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3500}/>

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-100 px-4 md:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Receipt className="text-indigo-600" size={22}/> Expense Manager
            </h1>
            <p className="text-gray-400 text-xs mt-0.5">{data.length} total records</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={fetchData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition" title="Refresh"><RefreshCw size={15}/></button>
            <button onClick={()=>setShowImport(true)} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition text-sm"><FileUp size={15}/>Import CSV</button>
            <button onClick={handleExport} disabled={!filteredData.length} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition text-sm disabled:opacity-40"><Download size={15}/>Export CSV</button>
            <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"><Plus size={15}/>Add Expense</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 mt-4 -mb-4 border-b-0">
          {TABS.map(({key,label,icon:Icon})=>(
            <button key={key} onClick={()=>setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition ${activeTab===key?"border-indigo-600 text-indigo-600":"border-transparent text-gray-500 hover:text-gray-700"}`}>
              <Icon size={15}/>{label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 md:px-6 py-5 space-y-5">

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* OVERVIEW TAB                                                    */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab==="overview" && (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label:"Total Expenses",  value:`₹${fmt(stats.total)}`,       sub:`${stats.count} transactions`,   icon:<DollarSign size={16} className="text-indigo-600"/>, bg:"bg-indigo-100" },
                { label:"This Month",      value:`₹${fmt(stats.monthTotal)}`,   sub:new Date().toLocaleString("default",{month:"long"}), icon:<Calendar size={16} className="text-purple-600"/>, bg:"bg-purple-100" },
                { label:"Today",           value:`₹${fmt(stats.todayTotal)}`,   sub:"Today's spending",              icon:<TrendingUp size={16} className="text-emerald-600"/>, bg:"bg-emerald-100" },
                { label:"Highest Entry",   value:`₹${fmt(stats.highest)}`,      sub:"Single expense",                icon:<AlertCircle size={16} className="text-rose-600"/>, bg:"bg-rose-100" },
              ].map(c=>(
                <div key={c.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500 font-medium">{c.label}</span>
                    <div className={`p-1.5 rounded-lg ${c.bg}`}>{c.icon}</div>
                  </div>
                  <p className="text-xl font-bold text-gray-900 truncate">{c.value}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>
                </div>
              ))}
            </div>

            {/* Month-over-Month */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                {mom.pct===null||mom.pct>=0 ? <TrendingUp size={16} className="text-rose-500"/> : <TrendingDown size={16} className="text-emerald-500"/>}
                Month-over-Month
              </h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-400">Last Month</p>
                  <p className="text-lg font-bold text-gray-700">₹{fmt(mom.lastM)}</p>
                </div>
                <div className="flex items-center justify-center">
                  {mom.pct===null ? <span className="text-xs text-gray-400">No prev data</span>
                    : <span className={`text-sm font-bold px-3 py-1 rounded-full ${mom.pct>=0?"bg-rose-100 text-rose-600":"bg-emerald-100 text-emerald-600"}`}>
                        {mom.pct>=0?"+":""}{mom.pct.toFixed(1)}%
                      </span>
                  }
                </div>
                <div className="flex-1 text-center p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                  <p className="text-xs text-indigo-400">This Month</p>
                  <p className="text-lg font-bold text-indigo-700">₹{fmt(mom.thisM)}</p>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
                <h2 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <BarChart2 size={16} className="text-indigo-600"/>
                  {chartView==="bar" ? "Monthly Spending (Last 6 Months)" : "Spending by Category"}
                </h2>
                <div className="flex gap-1">
                  <button onClick={()=>setChartView("bar")} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition ${chartView==="bar"?"bg-indigo-100 text-indigo-700":"text-gray-500 hover:bg-gray-100"}`}><BarChart2 size={13}/>Bar</button>
                  <button onClick={()=>setChartView("donut")} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition ${chartView==="donut"?"bg-indigo-100 text-indigo-700":"text-gray-500 hover:bg-gray-100"}`}><PieChart size={13}/>Donut</button>
                </div>
              </div>
              <div className="p-5">
                {chartView==="bar" ? (
                  <div className="h-52">
                    <Bar data={barData} options={{ responsive:true, maintainAspectRatio:false,
                      plugins:{ legend:{display:false}, tooltip:{callbacks:{label:ctx=>`₹${Number(ctx.parsed.y).toLocaleString("en-IN")}`}} },
                      scales:{ y:{beginAtZero:true,grid:{color:"#f3f4f6"},ticks:{callback:v=>`₹${Number(v).toLocaleString("en-IN")}`,font:{size:10}}}, x:{grid:{display:false},ticks:{font:{size:10}}} }
                    }}/>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="h-52 w-52 flex-shrink-0">
                      {donutData.labels.length ? <Doughnut data={donutData} options={{ responsive:true, maintainAspectRatio:false,
                        plugins:{ legend:{display:false}, tooltip:{callbacks:{label:ctx=>`${ctx.label}: ₹${Number(ctx.parsed).toLocaleString("en-IN")}`}} }
                      }}/> : <div className="h-full flex items-center justify-center text-gray-300 text-sm">No data</div>}
                    </div>
                    <div className="flex-1 space-y-2 w-full">
                      {donutData.labels.map((cat,i)=>{
                        const c=getCat(cat); const v=donutData.datasets[0].data[i];
                        const pct=stats.total>0?((v/stats.total)*100).toFixed(1):0;
                        return (
                          <div key={cat}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:c.color}}/>{cat}</span>
                              <span className="font-medium text-gray-700">₹{fmt(v)} <span className="text-gray-400">({pct}%)</span></span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full"><div className="h-1.5 rounded-full" style={{width:`${pct}%`,backgroundColor:c.color}}/></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* EXPENSES TAB                                                    */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab==="expenses" && (
          <>
            {/* Filter bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                  <input type="text" placeholder="Search title, description, tags..." value={search}
                    onChange={e=>{setSearch(e.target.value);setPage(1);}}
                    className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                  {search && <button onClick={()=>setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={13}/></button>}
                </div>
                <select value={categoryFilter} onChange={e=>{setCategoryFilter(e.target.value);setPage(1);}}
                  className="py-2 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="all">All Categories</option>
                  {CATEGORIES.map(c=><option key={c.value} value={c.value}>{c.value}</option>)}
                </select>
                <select value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1);}}
                  className="py-2 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                </select>
                <div className="flex gap-1 flex-wrap">
                  {PERIODS.map(p=>(
                    <button key={p.value} onClick={()=>{setPeriod(p.value);setPage(1);}}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition whitespace-nowrap ${period===p.value?"bg-indigo-600 text-white":"bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              {(search||categoryFilter!=="all"||statusFilter!=="all"||period!=="all") && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50 flex-wrap">
                  <span className="text-xs text-gray-400">Active:</span>
                  {period!=="all"&&<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">{PERIODS.find(p=>p.value===period)?.label}<button onClick={()=>setPeriod("all")}><X size={10}/></button></span>}
                  {categoryFilter!=="all"&&<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">{categoryFilter}<button onClick={()=>setCategoryFilter("all")}><X size={10}/></button></span>}
                  {statusFilter!=="all"&&<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">{statusFilter}<button onClick={()=>setStatusFilter("all")}><X size={10}/></button></span>}
                  {search&&<span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">"{search}"<button onClick={()=>setSearch("")}><X size={10}/></button></span>}
                  <button onClick={()=>{setSearch("");setCategoryFilter("all");setStatusFilter("all");setPeriod("all");}} className="text-xs text-gray-400 hover:text-gray-600 ml-auto">Clear all</button>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">#</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={()=>handleSort("date")}>Date<SortIcon col="date"/></th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={()=>handleSort("title")}>Title<SortIcon col="title"/></th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={()=>handleSort("price")}>Amount<SortIcon col="price"/></th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Tags</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {pageData.length===0 ? (
                      <tr><td colSpan={8} className="py-20 text-center">
                        <Receipt size={40} className="text-gray-200 mx-auto mb-3"/>
                        <p className="text-gray-400">{data.length>0?"No results — try adjusting filters":"No expenses yet"}</p>
                        {data.length===0&&<button onClick={openAdd} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition"><Plus size={14}/>Add First Expense</button>}
                      </td></tr>
                    ) : pageData.map((item,idx)=>{
                      const cat=getCat(item.category);
                      return (
                        <tr key={item._id} className="hover:bg-gray-50/70 transition group">
                          <td className="py-3 px-4 text-xs text-gray-400">{(page-1)*PAGE+idx+1}</td>
                          <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">{fmtDate(item.date)}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cat.badge}`}>
                              <span className="w-1.5 h-1.5 rounded-full" style={{backgroundColor:cat.color}}/>{item.category||"Miscellaneous"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-gray-800">
                            <div className="flex items-center gap-2">
                              {item.title||"—"}
                              {item.isRecurring&&<Repeat size={12} className="text-indigo-400" title={`Recurring ${item.recurringFrequency}`}/>}
                              {item.receiptUrl&&<a href={item.receiptUrl} target="_blank" rel="noreferrer"><Image size={12} className="text-blue-400" title="View receipt"/></a>}
                              {item.paymentMode==="credit"&&<span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-200" title={`Vendor: ${item.vendorName}`}>Credit</span>}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm font-semibold text-gray-900 whitespace-nowrap">₹{fmt(item.price)}</td>
                          <td className="py-3 px-4 hidden md:table-cell">
                            <div className="flex gap-1 flex-wrap">
                              {(item.tags||[]).map(t=><span key={t} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{t}</span>)}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {(item.status||"approved")==="approved"
                              ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle size={12}/>Approved</span>
                              : <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Clock size={12}/>Pending</span>}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              {(item.status||"approved")==="pending"&&(
                                <button onClick={()=>handleApprove(item)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition" title="Approve"><CheckCircle size={14}/></button>
                              )}
                              <button onClick={()=>openEdit(item)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Edit"><Edit2 size={14}/></button>
                              <button onClick={()=>handleDelete(item._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition" title="Delete"><Trash2 size={14}/></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages>1&&(
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
                  <p className="text-sm text-gray-500">Showing <span className="font-medium text-gray-700">{(page-1)*PAGE+1}–{Math.min(page*PAGE,filteredData.length)}</span> of <span className="font-medium text-gray-700">{filteredData.length}</span></p>
                  <div className="flex gap-1">
                    <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"><ChevronLeft size={16}/></button>
                    {(()=>{const s=Math.max(1,Math.min(page-2,totalPages-4)),e=Math.min(totalPages,s+4),r=[];for(let i=s;i<=e;i++)r.push(i);return r;})().map(p2=>(
                      <button key={p2} onClick={()=>setPage(p2)} className={`w-8 h-8 rounded-lg text-sm font-medium transition ${page===p2?"bg-indigo-600 text-white":"hover:bg-gray-100 text-gray-600"}`}>{p2}</button>
                    ))}
                    <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40"><ChevronRight size={16}/></button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* BUDGETS TAB                                                     */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab==="budgets" && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0"/>
              <div>
                <p className="text-sm font-medium text-amber-800">Monthly Budget Tracker</p>
                <p className="text-xs text-amber-600 mt-0.5">Set monthly spending limits per category. Alerts fire at 80% and when exceeded.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {budgetProgress.map(cat=>(
                <div key={cat.value} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cat.badge}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{backgroundColor:cat.color}}/>{cat.value}
                    </span>
                    {cat.isOver&&<span className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12}/>Over budget</span>}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                    <span>Spent: <span className="font-semibold text-gray-800">₹{fmt(cat.spent)}</span></span>
                    <span>Limit: <span className="font-semibold text-gray-800">{cat.limit>0?`₹${fmt(cat.limit)}`:"Not set"}</span></span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                    {cat.limit>0&&<div className={`h-2 rounded-full transition-all ${cat.isOver?"bg-red-500":cat.pct>80?"bg-amber-500":"bg-emerald-500"}`} style={{width:`${cat.pct}%`}}/>}
                  </div>
                  {cat.limit>0&&<p className="text-xs text-gray-400 mb-3">{cat.pct.toFixed(0)}% used{cat.limit>0&&` — ₹${fmt(Math.max(0,cat.limit-cat.spent))} remaining`}</p>}
                  {editingBudget===cat.value ? (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                        <input type="number" min="0" value={budgetInput} onChange={e=>setBudgetInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveBudget(cat.value)}
                          placeholder="Monthly limit" autoFocus
                          className="w-full pl-7 pr-3 py-2 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                      </div>
                      <button onClick={()=>saveBudget(cat.value)} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition">Save</button>
                      <button onClick={()=>{setEditingBudget(null);setBudgetInput("");}} className="px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={()=>{setEditingBudget(cat.value);setBudgetInput(cat.limit>0?cat.limit:"");}}
                      className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition">
                      {cat.limit>0?"Edit Budget":"+ Set Budget Limit"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* REPORTS TAB                                                     */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {activeTab==="reports" && (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><FileText size={16} className="text-indigo-600"/>Generate PDF Report</h3>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">From</label>
                  <input type="date" value={reportFrom} onChange={e=>setReportFrom(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">To</label>
                  <input type="date" value={reportTo} onChange={e=>setReportTo(e.target.value)} max={todayLocal()}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                </div>
              </div>
              <div className="flex items-center gap-4 mb-5">
                <div className="flex-1 p-3 bg-indigo-50 rounded-lg text-center">
                  <p className="text-xs text-indigo-400">Records</p>
                  <p className="text-xl font-bold text-indigo-700">{reportData.length}</p>
                </div>
                <div className="flex-1 p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-xs text-emerald-400">Total</p>
                  <p className="text-xl font-bold text-emerald-700">₹{fmt(reportData.reduce((s,i)=>s+(+i.price||0),0))}</p>
                </div>
                <button onClick={generatePDF} disabled={!reportData.length}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium disabled:opacity-40 text-sm">
                  <Download size={16}/>Download PDF
                </button>
              </div>
              {/* Preview table */}
              <div className="border border-gray-100 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">Preview</div>
                <div className="overflow-y-auto max-h-72">
                  <table className="min-w-full">
                    <thead><tr className="text-left text-xs text-gray-400">
                      {["Date","Category","Title","Amount"].map(h=><th key={h} className="px-4 py-2 font-medium">{h}</th>)}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-50">
                      {reportData.slice(0,50).map(item=>(
                        <tr key={item._id} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2 text-xs text-gray-600">{fmtDate(item.date)}</td>
                          <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs border ${getCat(item.category).badge}`}>{item.category||"Misc"}</span></td>
                          <td className="px-4 py-2 text-xs text-gray-800 font-medium">{item.title}</td>
                          <td className="px-4 py-2 text-xs font-semibold text-gray-900">₹{fmt(item.price)}</td>
                        </tr>
                      ))}
                      {reportData.length===0&&<tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-sm">No data in selected range</td></tr>}
                      {reportData.length>50&&<tr><td colSpan={4} className="px-4 py-2 text-center text-xs text-gray-400">+{reportData.length-50} more rows in PDF</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Category summary for report */}
            {reportData.length>0&&(
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-800 mb-4 text-sm">Category Breakdown</h3>
                <div className="space-y-3">
                  {CATEGORIES.map(cat=>{
                    const total=reportData.filter(i=>(i.category||"Miscellaneous")===cat.value).reduce((s,i)=>s+(+i.price||0),0);
                    if(!total)return null;
                    const grandTotal=reportData.reduce((s,i)=>s+(+i.price||0),0);
                    const pct=grandTotal>0?((total/grandTotal)*100).toFixed(1):0;
                    return(
                      <div key={cat.value}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{backgroundColor:cat.color}}/>{cat.value}</span>
                          <span className="font-semibold text-gray-800">₹{fmt(total)} <span className="text-gray-400">({pct}%)</span></span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full"><div className="h-1.5 rounded-full" style={{width:`${pct}%`,backgroundColor:cat.color}}/></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ADD / EDIT MODAL                                                   */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {showModal&&(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={e=>{if(e.target===e.currentTarget)closeModal();}}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{isUpdateMode?"Edit Expense":"New Expense"}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{isUpdateMode?"Update the expense details":"Fill in the expense details"}</p>
              </div>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg transition text-gray-400"><X size={17}/></button>
            </div>

            {/* Duplicate warning */}
            {dupWarning&&(
              <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0"/>
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-800">Possible Duplicate Detected</p>
                  <p className="text-xs text-amber-600 mt-0.5">"{dupWarning.title}" — ₹{fmt(dupWarning.price)} on {fmtDate(dupWarning.date)}</p>
                </div>
                <button onClick={()=>{setDupWarning(null);document.querySelector("form")?.requestSubmit();}} className="text-xs bg-amber-500 text-white px-2 py-1 rounded-lg hover:bg-amber-600 transition flex-shrink-0">Add anyway</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {/* Date + Amount */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Date</label>
                  <input id="date" type="date" value={form.date} onChange={handleChange} max={todayLocal()} required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Amount (₹)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                    <input id="price" type="number" min="0" step="0.01" value={form.price} onChange={handleChange} placeholder="0.00" required
                      className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Title</label>
                <input id="title" type="text" value={form.title} onChange={handleChange} placeholder="e.g. Monthly electricity bill" required
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Category</label>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map(c=>(
                    <button key={c.value} type="button" onClick={()=>setForm(p=>({...p,category:c.value}))}
                      className={`px-2.5 py-1 rounded-full text-xs border transition ${form.category===c.value?`${c.badge} font-semibold shadow-sm`:"bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
                      {c.value}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide flex items-center gap-1"><Tag size={11}/>Tags <span className="font-normal text-gray-400 normal-case">(Enter or comma to add)</span></label>
                <div className="border border-gray-200 rounded-lg p-2 flex flex-wrap gap-1.5 min-h-[40px] focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent">
                  {form.tags.map(t=>(
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">
                      {t}<button type="button" onClick={()=>removeTag(t)}><X size={10}/></button>
                    </span>
                  ))}
                  <input type="text" value={tagInput} onChange={e=>setTagInput(e.target.value)} onKeyDown={handleTagKey}
                    placeholder={form.tags.length?"":"Add tags..."}
                    className="flex-1 min-w-[80px] outline-none text-sm bg-transparent"/>
                </div>
              </div>

              {/* Recurring */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={form.isRecurring} onChange={e=>setForm(p=>({...p,isRecurring:e.target.checked}))} className="w-4 h-4 accent-indigo-600"/>
                  <span className="text-sm text-gray-700 flex items-center gap-1"><Repeat size={13} className="text-indigo-500"/>Recurring Expense</span>
                </label>
                {form.isRecurring&&(
                  <select id="recurringFrequency" value={form.recurringFrequency} onChange={handleChange}
                    className="ml-auto border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                )}
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</label>
                <div className="flex gap-2">
                  {["approved","pending"].map(s=>(
                    <button key={s} type="button" onClick={()=>setForm(p=>({...p,status:s}))}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition ${form.status===s
                        ? s==="approved"?"bg-emerald-100 text-emerald-700 border-emerald-200":"bg-amber-100 text-amber-700 border-amber-200"
                        : "bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
                      {s==="approved"?<CheckCircle size={12}/>:<Clock size={12}/>}{s.charAt(0).toUpperCase()+s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment mode — Pay Later creates a vendor ledger payable */}
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                <div className="flex items-center gap-3">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Payment</label>
                  <div className="flex gap-2">
                    {[{v:"paid",label:"Paid Now"},{v:"credit",label:"Pay Later (Credit)"}].map(({v,label})=>(
                      <button key={v} type="button" onClick={()=>setForm(p=>({...p,paymentMode:v,vendorName:v==="paid"?"":p.vendorName}))}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition ${form.paymentMode===v
                          ? v==="paid"?"bg-emerald-100 text-emerald-700 border-emerald-200":"bg-rose-100 text-rose-700 border-rose-200"
                          : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
                        {v==="paid"?<CheckCircle size={12}/>:<Clock size={12}/>}{label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.paymentMode==="credit"&&(
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Vendor / Supplier Name *</label>
                    <input type="text" value={form.vendorName} onChange={e=>setForm(p=>({...p,vendorName:e.target.value}))}
                      placeholder="e.g. ABC Wholesale, Fresh Foods Ltd."
                      className="w-full border border-rose-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"/>
                    <p className="text-xs text-rose-500 mt-1">Will auto-create a vendor payable in Ledger</p>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Description <span className="font-normal text-gray-400 normal-case">(optional)</span></label>
                <textarea id="description" value={form.description} onChange={handleChange} placeholder="Any notes..." rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"/>
              </div>

              {/* Receipt upload */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide flex items-center gap-1"><Image size={11}/>Receipt / Bill Photo <span className="font-normal text-gray-400 normal-case">(optional)</span></label>
                {form.receiptUrl ? (
                  <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <CheckCircle size={14} className="text-emerald-500"/>
                    <a href={form.receiptUrl} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline flex-1 truncate">View uploaded receipt</a>
                    <button type="button" onClick={()=>setForm(p=>({...p,receiptUrl:null}))} className="text-gray-400 hover:text-red-400"><X size={13}/></button>
                  </div>
                ) : (
                  <div className="relative">
                    <input ref={receiptRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e=>handleReceiptUpload(e.target.files[0])}/>
                    <button type="button" onClick={()=>receiptRef.current?.click()} disabled={uploadingReceipt}
                      className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition disabled:opacity-60">
                      {uploadingReceipt?<><div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin"/>Uploading...</>:<><Upload size={14}/>Upload receipt photo or PDF</>}
                    </button>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={closeModal} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 text-sm font-medium hover:bg-gray-50 transition">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition disabled:opacity-60 flex items-center justify-center gap-2">
                  {isSubmitting?<><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/>Saving...</>:isUpdateMode?"Update Expense":"Add Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* BULK IMPORT MODAL                                                  */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {showImport&&(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={e=>{if(e.target===e.currentTarget){setShowImport(false);setImportPreview([]);}}}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Bulk Import via CSV</h2>
                <p className="text-xs text-gray-400 mt-0.5">Required columns: Date, Title, Amount. Optional: Category, Description, Tags</p>
              </div>
              <button onClick={()=>{setShowImport(false);setImportPreview([]);}} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400"><X size={17}/></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <input ref={importRef} type="file" accept=".csv" className="hidden" onChange={handleCSVParse}/>
                <button onClick={()=>importRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition">
                  <FileUp size={28}/>
                  <span className="text-sm font-medium">Click to upload CSV file</span>
                  <span className="text-xs text-gray-400">or drag and drop</span>
                </button>
              </div>
              {importPreview.length>0&&(
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{importPreview.length} rows ready to import</span>
                    <button onClick={()=>setImportPreview([])} className="text-xs text-gray-400 hover:text-red-400">Clear</button>
                  </div>
                  <div className="border border-gray-100 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0"><tr>
                        {["Date","Category","Title","Amount"].map(h=><th key={h} className="px-3 py-2 text-left font-semibold text-gray-500">{h}</th>)}
                      </tr></thead>
                      <tbody className="divide-y divide-gray-50">
                        {importPreview.slice(0,20).map((row,i)=>(
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-gray-600">{row.Date||row.date||"—"}</td>
                            <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded-full border ${getCat(row.Category||row.category).badge}`}>{row.Category||row.category||"Misc"}</span></td>
                            <td className="px-3 py-2 font-medium text-gray-800">{row.Title||row.title}</td>
                            <td className="px-3 py-2 font-semibold">₹{parseFloat(row["Amount (₹)"]||row.Amount||row.price||0).toFixed(2)}</td>
                          </tr>
                        ))}
                        {importPreview.length>20&&<tr><td colSpan={4} className="px-3 py-2 text-center text-gray-400">+{importPreview.length-20} more rows</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={()=>{setShowImport(false);setImportPreview([]);}} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 transition">Cancel</button>
                    <button onClick={handleBulkImport} disabled={importing}
                      className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition disabled:opacity-60 flex items-center justify-center gap-2">
                      {importing?<><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/>Importing...</>:<><Upload size={14}/>Import {importPreview.length} Expenses</>}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expensises;
